import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppear } from '../hooks/useAppear';
import Img, { GDriveVideo, VideoPlaceholder } from '../components/ui/Img';
import { galleryPhotos, videos } from '../data';
import { IcoExpand, IcoClose, IcoPlay, IcoImage, IcoArrow } from '../assets/icons';

const CAT_KEYS = ['all', 'community', 'health', 'women', 'youth', 'elderly'];
const CAT_PT   = ['Todos', 'Comunidade', 'Saúde', 'Mulheres', 'Jovens', 'Idosos'];
const CAT_EN   = ['All',   'Community',  'Health', 'Women',   'Youth',  'Elderly'];

/* ── Lightbox ──────────────────────────────────────────── */
function Lightbox({ item, onClose, isPt }) {
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [onClose]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-brand-950/95 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 flex items-center gap-2 text-brand-400 hover:text-white transition-colors text-sm font-medium"
        >
          <IcoClose cls="w-5 h-5" />
          {isPt ? 'Fechar' : 'Close'}
        </button>

        {/* Image */}
        <div className="rounded-2xl overflow-hidden border border-brand-800">
          <Img
            src={item.src}
            alt={isPt ? item.caption_pt : item.caption_en}
            label={item.label}
            className="w-full max-h-[76vh]"
            style={{ objectFit: 'contain' }}
          />
        </div>

        {/* Caption */}
        <div className="mt-4 flex items-center justify-between px-1">
          <p className="text-brand-300 text-sm font-medium">
            {isPt ? item.caption_pt : item.caption_en}
          </p>
          <span className="text-[11px] text-brand-600 uppercase tracking-wider font-bold">
            {CAT_PT[CAT_KEYS.indexOf(item.cat)] || item.cat}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Page Hero ─────────────────────────────────────────── */
function PageHero({ isPt }) {
  return (
    <section className="relative bg-brand-950 pt-[68px] overflow-hidden">
      <div className="absolute inset-0">
        <Img
          src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9457_1.JPG"
          alt="Acções Sociais"
          className="w-full h-full"
          style={{ objectFit: 'cover', opacity: 0.15 }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-950/60 via-brand-950/80 to-brand-950" />
      </div>
      {/* Decorative geometry */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="aph" x="0" y="0" width="56" height="56" patternUnits="userSpaceOnUse">
              <path d="M28 4 L52 52 L4 52 Z" fill="none" stroke="#ea580c" strokeWidth="0.6"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#aph)"/>
        </svg>
      </div>
      <div className="wrap relative z-10 py-24 text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-800 bg-brand-900/60 mb-7">
          <span className="text-[11px] font-bold text-brand-500 uppercase tracking-[0.14em]">
            {isPt ? 'Galeria de Acções' : 'Actions Gallery'}
          </span>
        </div>
        <h1 className="display-xl text-white mb-6">
          {isPt ? 'Acções Sociais' : 'Social Actions'}
        </h1>
        <p className="body-lg text-brand-400 max-w-xl mx-auto">
          {isPt
            ? 'Fotos e vídeos das nossas acções nas comunidades angolanas. Cada imagem conta uma história real de transformação.'
            : 'Photos and videos of our actions in Angolan communities. Each image tells a real story of transformation.'}
        </p>
      </div>
    </section>
  );
}

/* ── Videos Section ────────────────────────────────────── */
function VideosSection({ isPt }) {
  const [main, setMain] = useState(videos[0]);
  const ref = useAppear();

  return (
    <section id="videos" className="section-pad bg-brand-950">
      <div className="wrap">
        <div ref={ref} className="mb-10">
          <p className="label-tag sec-tag text-brand-500">{isPt ? 'Vídeos' : 'Videos'}</p>
          <h2 className="display-md text-white">
            {isPt ? 'Documentais e Reportagens' : 'Documentaries & Reports'}
          </h2>
         
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main player */}
          <div className="lg:col-span-2 space-y-4">
            <VideoPlaceholder
              src={main.src}
              title={isPt ? main.title_pt : main.title_en}
              className="w-full aspect-video"
            />
            <div>
              <h3 className="text-[16px] font-bold text-white mb-1">
                {isPt ? main.title_pt : main.title_en}
              </h3>
              <p className="text-sm text-brand-500">
                {isPt ? main.desc_pt : main.desc_en}
              </p>
              <p className="text-[11px] text-brand-700 mt-2">
                {main.label}
              </p>
            </div>
          </div>

          {/* Video list */}
          <div className="flex flex-col gap-3">
            {videos.map((v) => (
              <button
                key={v.id}
                onClick={() => setMain(v)}
                className={`text-left p-4 rounded-xl border transition-all group ${
                  main.id === v.id
                    ? 'border-brand-600 bg-brand-900'
                    : 'border-brand-900 bg-brand-900/40 hover:border-brand-700 hover:bg-brand-900'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center transition-colors ${
                    main.id === v.id ? 'bg-brand-600' : 'bg-brand-800 group-hover:bg-brand-700'
                  }`}>
                    <IcoPlay cls="w-4 h-4 text-white ml-0.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-white leading-snug line-clamp-2">
                      {isPt ? v.title_pt : v.title_en}
                    </p>
                    <p className="text-[11px] text-brand-600 mt-1 line-clamp-1">
                      {isPt ? v.desc_pt : v.desc_en}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Photo Gallery ─────────────────────────────────────── */
function PhotoGallery({ isPt }) {
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const ref = useAppear();

  const cats = isPt ? CAT_PT : CAT_EN;
  const filtered = filter === 'all'
    ? galleryPhotos
    : galleryPhotos.filter((p) => p.cat === filter);

  return (
    <section id="galeria" className="section-pad bg-white dark:bg-brand-950">
      <div className="wrap">
        {/* Header + filters */}
        <div ref={ref} className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <p className="label-tag sec-tag">{isPt ? 'Galeria' : 'Gallery'}</p>
            <h2 className="display-md text-brand-900 dark:text-white">
              {isPt ? 'Fotos em Campo' : 'Field Photos'}
            </h2>
          </div>
          {/* Filter pills */}
          <div className="flex flex-wrap gap-2">
            {cats.map((cat, i) => (
              <button
                key={i}
                onClick={() => setFilter(CAT_KEYS[i])}
                className={`px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all border ${
                  filter === CAT_KEYS[i]
                    ? 'bg-brand-600 border-brand-600 text-white'
                    : 'border-brand-200 dark:border-brand-800 text-brand-600 dark:text-brand-500 hover:border-brand-400 hover:text-brand-700'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Masonry grid */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
          {filtered.map((photo) => {
            const heightCls =
              photo.size === 'lg' ? 'h-72' :
              photo.size === 'md' ? 'h-52' : 'h-40';

            return (
              <div
                key={photo.id}
                className="break-inside-avoid mb-4 cursor-pointer group relative overflow-hidden rounded-xl border border-brand-100 dark:border-brand-900"
                onClick={() => setLightbox(photo)}
              >
                <Img
                  src={photo.src}
                  alt={isPt ? photo.caption_pt : photo.caption_en}
                  label={photo.label}
                  className={`w-full ${heightCls}`}
                />

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-900/90 via-brand-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex items-end p-4">
                  <div className="flex items-end justify-between w-full">
                    <div>
                      <p className="text-white text-sm font-semibold leading-tight">
                        {isPt ? photo.caption_pt : photo.caption_en}
                      </p>
                      <p className="text-brand-300 text-[11px] mt-0.5 uppercase tracking-wider">
                        {cats[CAT_KEYS.indexOf(photo.cat)] || photo.cat}
                      </p>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center flex-shrink-0 ml-3">
                      <IcoExpand cls="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

       
      </div>

      <Lightbox item={lightbox} onClose={() => setLightbox(null)} isPt={isPt} />
    </section>
  );
}

/* ── Stories Section ───────────────────────────────────── */
function StoriesSection({ isPt }) {
  const ref = useAppear();
  const stories = [
    {
      province_pt: 'Toco, Huíla',
      province_en: 'Toco, Huíla',
      programme_pt: 'PDIC',
      programme_en: 'PDIC',
      story_pt: 'Graças ao PDIC, a nossa comunidade tem agora água potável, uma escola reabilitada e assistência médica regular. O Toco mudou completamente.',
      story_en: 'Thanks to the PDIC, our community now has clean water, a rehabilitated school and regular medical assistance. Toco has changed completely.',
    },
    {
      province_pt: 'Luanda',
      province_en: 'Luanda',
      programme_pt: 'Seja Mulher',
      programme_en: 'Seja Mulher',
      story_pt: 'O programa Seja Mulher deu-me formação, confiança e as ferramentas para criar o meu próprio negócio. Hoje sou independente e inspiração para outras mulheres.',
      story_en: 'The Seja Mulher programme gave me training, confidence and the tools to create my own business. Today I am independent and an inspiration to other women.',
    },
    {
      province_pt: 'Malanje',
      province_en: 'Malanje',
      programme_pt: 'Cirurgias Pé Boto',
      programme_en: 'Clubfoot Surgery',
      story_pt: 'O meu filho nasceu com pé boto. Graças ao programa da Fundação, ele foi operado e hoje corre e brinca como qualquer criança.',
      story_en: 'My son was born with clubfoot. Thanks to the Foundation\'s programme, he had surgery and today he runs and plays like any child.',
    },
  ];

  return (
    <section id="historias" className="section-pad bg-brand-50 dark:bg-brand-900/20">
      <div className="wrap">
        <div ref={ref} className="text-center mb-14">
          <p className="label-tag sec-tag justify-center">
            {isPt ? 'Histórias de Impacto' : 'Impact Stories'}
          </p>
          <h2 className="display-md text-brand-900 dark:text-white">
            {isPt ? 'Pessoas Reais, Mudanças Reais' : 'Real People, Real Change'}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {stories.map((s, i) => (
            <div
              key={i}
              className="card-base overflow-hidden hover:-translate-y-1 transition-transform"
            >
              {/* Photo */}
              <Img
                src={[
                  'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9454_1.JPG',
                  'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/FOTOS%20TATA%20UHAYELE/23.jpg',
                  'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/11.jpg.jpeg',
                ][i]}
                alt={s.name}
                className="w-full h-52"
              />
              {/* Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-bold text-brand-900 dark:text-white text-[15px]">{s.name}</h4>
                    <p className="text-brand-500 text-[12px] mt-0.5">
                      {isPt ? s.province_pt : s.province_en}
                    </p>
                  </div>
                  <span className="text-[10px] font-bold text-brand-600 bg-brand-50 dark:bg-brand-900 border border-brand-200 dark:border-brand-800 px-2.5 py-1 rounded-full uppercase tracking-wider">
                    {isPt ? s.programme_pt : s.programme_en}
                  </span>
                </div>
                {/* Orange divider */}
                <div className="h-px bg-brand-100 dark:bg-brand-900 mb-4" />
                <p className="text-sm text-brand-700 dark:text-brand-400 leading-relaxed">
                  {isPt ? s.story_pt : s.story_en}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Page ──────────────────────────────────────────────── */
export default function ActionsPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language === 'pt';

  return (
    <main>
      <PageHero   isPt={isPt} />
      <VideosSection isPt={isPt} />
      <PhotoGallery  isPt={isPt} />
      <StoriesSection isPt={isPt} />
    </main>
  );
}
